import { TableRowModel } from '@examdojo/supabase';
import { ContentItem } from '../content-item';
import { StemMarkScheme } from '../markscheme-v2';
import { QuestionImageStoreModel } from './question-image.model';
import { StemDifficultyCategory } from './stem-difficulty-category.model';
import { StemUIModel } from './stem.model';
import { StimulusUIModel } from './stimulus.model';

export type QuestionHttpModel = Pick<
  TableRowModel<'questions'>,
  | 'id'
  | 'question_number'
  | 'author'
  | 'items'
  | 'llm_feedback'
  | 'metadata'
  | 'created_at'
  | 'status'
  | 'updated_at'
  | 'updated_by'
  | 'question_items'
  | 'reference_question_id'
  | 'max_points'
> & {
  /** Difficulty calculated from derivative_questions view based on user's syllabus and course level */
  difficulty?: StemDifficultyCategory | null;
  attempted?: boolean | null;
};

export type QuestionItemType = 'stimulus' | 'stem' | 'question_image';

export type QuestionItemQuestionImageStoreModel = QuestionImageStoreModel & {
  questionImageUrl: string | null;
};

export type QuestionItemQuestionImageUIModel = QuestionItemQuestionImageStoreModel;

export type QuestionItemsUIModel = Array<StimulusUIModel | StemUIModel | QuestionItemQuestionImageUIModel>;

export type QuestionStoreModel = Omit<
  QuestionHttpModel,
  'sys_period' | 'author' | 'items' | 'llm_feedback' | 'question_items' | 'metadata'
> & {
  authorId: QuestionHttpModel['author'];
  llm_feedback: QuestionLLMFeedback;
  metadata: {
    calculator_allowed?: boolean;
  } | null;

  /** V1 question items. */
  items: Array<{ item_id: number; item_type: QuestionItemType }>;

  /** V2 question items. */
  question_items?: QuestionItemV2[];
};

export interface QuestionLLMFeedback {
  thinking: string;
  stems_feedback: StemLlmFeedback[];
  markscheme_feedback: string;
  everything_is_correct: boolean;
}
export interface StemLlmFeedback {
  stem_id: number;
  feedback: string;
  stem_letter: string;
}

export interface QuestionItemBase<T extends QuestionItemType> {
  type: T;
}

export enum QuestionGradingPreset {
  NoAnswer = 'no_answer',
  SomeMarks = 'some_marks',
  FullMarks = 'full_marks',
  GradingError = 'grading_error',
}

export const QUESTION_GRADING_PRESET_OPTIONS: Array<{ preset: QuestionGradingPreset; label: string; tooltip: string }> =
  [
    { preset: QuestionGradingPreset.NoAnswer, label: '❌', tooltip: 'No answer ❌' },
    { preset: QuestionGradingPreset.GradingError, label: '🔴', tooltip: 'Grading error (self-grading) 🔴' },
    { preset: QuestionGradingPreset.SomeMarks, label: ' 🟡', tooltip: 'Some marks 🟡' },
    { preset: QuestionGradingPreset.FullMarks, label: '🟢', tooltip: 'All marks 🟢' },
  ];

export const SUBSTEM_TAG_VALUES = [
  'i',
  'ii',
  'iii',
  'iv',
  'v',
  'vi',
  'vii',
  'viii',
  'ix',
  'x',
  'xi',
  'xii',
  'xiii',
  'xiv',
  'xv',
  'xvi',
  'xvii',
  'xviii',
  'xix',
  'xx',
];

export function getOrderedSubstemTagValue(orderNumber: number): string {
  if (orderNumber < 0 || orderNumber > SUBSTEM_TAG_VALUES.length) {
    return 'i';
  }

  return SUBSTEM_TAG_VALUES[orderNumber];
}

export function getOrderedItemTagValue(orderNumber: number): string {
  return String.fromCharCode(65 + orderNumber);
}

export type QuestionItemV2 = StimulusItemV2 | StemItemV2 | StemGroupItemV2 | OptionsItem;

export enum QuestionItemTypeEnum {
  Stimulus = 'stimulus',
  Stem = 'stem',
  StemGroup = 'stem_group',
  Options = 'options',
}

export const QUESTION_ITEM_TYPES = [
  QuestionItemTypeEnum.Stem,
  QuestionItemTypeEnum.Stimulus,
  QuestionItemTypeEnum.StemGroup,
  QuestionItemTypeEnum.Options,
];

export interface StimulusItemV2 {
  type: QuestionItemTypeEnum.Stimulus;
  content_items: ContentItem[];
}

export interface StemItemV2 {
  type: QuestionItemTypeEnum.Stem;
  content_items: ContentItem[];
  markscheme: StemMarkScheme | null;
}

export interface StemGroupItemV2 {
  type: QuestionItemTypeEnum.StemGroup;
  content_items: ContentItem[];
  stems: StemItemV2[];
}

export interface Option {
  content_items: ContentItem[];
}

export interface OptionsMarkscheme {
  marks: number;
  correct_option_indexes: number[];
  max_selection_count?: number;
  min_selection_count?: number;
  topic_ids: number[];
  worked_solution?: ContentItem[];
}

export interface OptionsItem {
  type: QuestionItemTypeEnum.Options;
  content_items: ContentItem[];
  options: Option[];
  markscheme: OptionsMarkscheme;
}
